import {IdentityAnchorFields, type VisualIdentity} from './IdentityAnchorFields'
import { useMemo, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil, Plus, Trash2, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { characterApi } from '@/api/characterApi'
import { ingestReviewApi } from '@/api/ingestReviewApi'
import { useUiStore } from '@/stores/uiStore'
import { friendlyMessage } from '@/api/http'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { VoiceInputButton } from '@/components/voice/VoiceInputButton'
import type { Character, CharacterInput, Project } from '@/types/nf'

type CharacterFormValues = {
  visual_identity: VisualIdentity
  name: string
  name_en: string
  aliases: string[]
  role: string
  age: string
  gender: string
  is_organization: boolean
  role_type: string
  personality: string
  background: string
  appearance: string
  relationships: string
  inner_flaw: string
  identity_situation: string
  realm: string
  realm_level: string
  motivation: string
  voice_signature: string
  traits: string[]
  organization_type: string
  organization_purpose: string
  organization_members: string
  notes: string
  avatar_url: string
  status: string
}

const emptyForm: CharacterFormValues = {
  visual_identity: {},
  name: '',
  name_en: '',
  aliases: [],
  role: '',
  age: '',
  gender: '',
  is_organization: false,
  role_type: '',
  personality: '',
  background: '',
  appearance: '',
  relationships: '',
  inner_flaw: '',
  identity_situation: '',
  realm: '',
  realm_level: '',
  motivation: '',
  voice_signature: '',
  traits: [],
  organization_type: '',
  organization_purpose: '',
  organization_members: '',
  notes: '',
  avatar_url: '',
  status: '',
}

function parseTags(value: string | null): string[] {
  if (!value) return []
  try {
    const parsed = JSON.parse(value)
    if (Array.isArray(parsed)) return parsed.filter((item): item is string => typeof item === 'string')
  } catch {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
  }
  return []
}

function toFormValues(character: Character): CharacterFormValues {
  return {
    name: character.name,
    name_en: character.name_en ?? '',
    aliases: parseTags(character.aliases),
    role: character.role ?? '',
    age: character.age ?? '',
    gender: character.gender ?? '',
    is_organization: character.is_organization === 1,
    role_type: character.role_type ?? '',
    personality: character.personality ?? '',
    background: character.background ?? '',
    visual_identity: character.visual_identity ?? {},
    appearance: character.appearance ?? '',
    relationships: character.relationships ?? '',
    inner_flaw: character.inner_flaw ?? '',
    identity_situation: character.identity_situation ?? '',
    realm: character.realm ?? '',
    realm_level: character.realm_level == null ? '' : String(character.realm_level),
    motivation: character.motivation ?? '',
    voice_signature: character.voice_signature ?? '',
    traits: parseTags(character.traits),
    organization_type: character.organization_type ?? '',
    organization_purpose: character.organization_purpose ?? '',
    organization_members: character.organization_members ?? '',
    notes: character.notes ?? '',
    avatar_url: character.avatar_url ?? '',
    status: character.status ?? '',
  }
}

function nullableText(value: string): string | null {
  const trimmed = value.trim()
  return trimmed ? trimmed : null
}

function toInput(values: CharacterFormValues): CharacterInput {
  const realmLevel = values.realm_level.trim()
  return {
    name: values.name.trim(),
    name_en: values.name_en.trim(),
    aliases: values.aliases,
    role: nullableText(values.role),
    age: nullableText(values.age),
    gender: nullableText(values.gender),
    is_organization: values.is_organization ? 1 : 0,
    role_type: nullableText(values.role_type),
    personality: nullableText(values.personality),
    background: nullableText(values.background),
    visual_identity: values.visual_identity,
    appearance: nullableText(values.appearance),
    relationships: nullableText(values.relationships),
    inner_flaw: nullableText(values.inner_flaw),
    identity_situation: nullableText(values.identity_situation),
    realm: nullableText(values.realm),
    realm_level: realmLevel ? Number(realmLevel) : null,
    motivation: nullableText(values.motivation),
    voice_signature: nullableText(values.voice_signature),
    traits: values.traits,
    organization_type: values.is_organization ? nullableText(values.organization_type) : null,
    organization_purpose: values.is_organization ? nullableText(values.organization_purpose) : null,
    organization_members: values.is_organization ? nullableText(values.organization_members) : null,
    notes: nullableText(values.notes),
    avatar_url: nullableText(values.avatar_url),
    status: nullableText(values.status),
  }
}

function Field({
  id,
  label,
  value,
  disabled,
  multiline = false,
  type = 'text',
  onChange,
  action,
}: {
  id: string
  label: string
  value: string
  disabled: boolean
  multiline?: boolean
  type?: string
  onChange: (value: string) => void
  action?: ReactNode
}) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label htmlFor={id} className="text-xs text-muted-foreground">
          {label}
        </Label>
        {action}
      </div>
      {multiline ? (
        <textarea
          id={id}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          rows={3}
          className="min-h-20 w-full resize-y rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30"
        />
      ) : (
        <Input
          id={id}
          type={type}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
        />
      )}
    </div>
  )
}

function TagInput({
  id,
  label,
  values,
  disabled,
  onChange,
}: {
  id: string
  label: string
  values: string[]
  disabled: boolean
  onChange: (values: string[]) => void
}) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState('')
  const addTag = () => {
    const tag = draft.trim()
    if (!tag || values.includes(tag)) return
    onChange([...values, tag])
    setDraft('')
  }
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return
    event.preventDefault()
    addTag()
  }
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-xs text-muted-foreground">
        {label}
      </Label>
      <div className="flex flex-wrap gap-2 rounded-md border border-input bg-transparent p-2 shadow-xs focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50 dark:bg-input/30">
        {values.map((value) => (
          <Badge key={value} variant="secondary" className="gap-1">
            {value}
            <button
              type="button"
              className="rounded-full outline-none hover:text-destructive focus-visible:ring-2 focus-visible:ring-ring"
              disabled={disabled}
              onClick={() => onChange(values.filter((item) => item !== value))}
              aria-label={t('character.removeTag', { value })}
            >
              <X className="size-3" />
            </button>
          </Badge>
        ))}
        <input
          id={id}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={onKeyDown}
          onBlur={addTag}
          disabled={disabled}
          placeholder={t('character.tagPlaceholder')}
          className="min-w-28 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
        />
      </div>
    </div>
  )
}

function FormSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <p className="text-xs font-medium uppercase text-muted-foreground">{title}</p>
      <div className="grid gap-3 sm:grid-cols-2">{children}</div>
    </section>
  )
}

export function CharacterPanel({ project }: { project: Project }) {
  const branchId = useUiStore((state) => state.currentBranchId)
  const pendingReview = useQuery({ queryKey: ['entity-review-settings', project.id, branchId], queryFn: () => ingestReviewApi.settings(project.id, branchId) })
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const queryKey = useMemo(() => ['characters', project.id] as const, [project.id])
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<Character | null>(null)
  const [values, setValues] = useState<CharacterFormValues>(emptyForm)

  const charactersQuery = useQuery({
    queryKey,
    queryFn: () => characterApi.list(project.id),
  })

  const createMutation = useMutation({
    mutationFn: (input: CharacterInput) => characterApi.create(project.id, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey })
      toast.success(t('character.created'))
      setOpen(false)
    },
    onError: (error) => toast.error(friendlyMessage(error, t('character.saveFailed'))),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: CharacterInput }) => characterApi.update(project.id, id, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey })
      toast.success(t('character.updated'))
      setOpen(false)
    },
    onError: (error) => toast.error(friendlyMessage(error, t('character.saveFailed'))),
  })

  const deleteMutation = useMutation({
    mutationFn: (characterId: string) => characterApi.delete(project.id, characterId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey })
      toast.success(t('character.deleted'))
    },
    onError: (error) => toast.error(friendlyMessage(error, t('character.deleteFailed'))),
  })

  const isSaving = createMutation.isPending || updateMutation.isPending
  const characters = charactersQuery.data ?? []

  const updateValue = <K extends keyof CharacterFormValues>(key: K, value: CharacterFormValues[K]) => {
    setValues((current) => ({ ...current, [key]: value }))
  }

  const openCreate = () => {
    setEditing(null)
    setValues(emptyForm)
    setOpen(true)
  }

  const openEdit = (character: Character) => {
    setEditing(character)
    setValues(toFormValues(character))
    setOpen(true)
  }

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!values.name.trim() || isSaving) return
    const input = toInput(values)
    if (editing) updateMutation.mutate({ id: editing.id, input })
    else createMutation.mutate(input)
  }

  const requestDelete = (character: Character) => {
    if (deleteMutation.isPending) return
    if (!window.confirm(t('character.deleteConfirm', { name: character.name }))) return
    deleteMutation.mutate(character.id)
  }

  return (
    <section className="space-y-3">
      {(pendingReview.data?.pending_count ?? 0) > 0 ? <a className="text-sm underline" href="?tab=ingestReview">{t('ingestReview.identity.pending', { count: pendingReview.data?.pending_count })}</a> : null}
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-medium uppercase text-muted-foreground">{t('character.title')}</p>
        <Button type="button" size="sm" variant="outline" onClick={openCreate}>
          <Plus className="size-4" />
          {t('character.new')}
        </Button>
      </div>

      {charactersQuery.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-20 rounded-lg" />
          <Skeleton className="h-20 rounded-lg" />
        </div>
      ) : charactersQuery.error ? (
        <div className="rounded-lg border border-dashed bg-background/45 p-4 text-sm text-muted-foreground">
          {friendlyMessage(charactersQuery.error, t('character.loadFailed'))}
        </div>
      ) : characters.length === 0 ? (
        <div className="rounded-lg border border-dashed bg-background/45 p-4 text-sm text-muted-foreground">
          {t('character.empty')}
        </div>
      ) : (
        <div className="space-y-2">
          {characters.map((character) => (
            <Card key={character.id} className="gap-0 rounded-lg py-0 shadow-xs">
              <CardContent className="p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="truncate text-sm font-medium">{character.name}</p>
                      {character.role === 'protagonist' ? <Badge>{t('character.protagonist')}</Badge> : null}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {character.role || character.role_type || t('character.roleUnset')}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <Button type="button" size="icon" variant="ghost" onClick={() => openEdit(character)}>
                      <Pencil className="size-4" />
                      <span className="sr-only">{t('common.edit')}</span>
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      disabled={deleteMutation.isPending}
                      onClick={() => requestDelete(character)}
                    >
                      <Trash2 className="size-4" />
                      <span className="sr-only">{t('character.delete')}</span>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>{editing ? t('character.editTitle') : t('character.createTitle')}</DialogTitle>
            <DialogDescription>{t('character.dialogDescription')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-5">
            <FormSection title={t('character.groups.basic')}>
              <Field
                id="character-name"
                label={t('character.fields.name')}
                value={values.name}
                disabled={isSaving}
                onChange={(value) => updateValue('name', value)}
              />
              <Field
                id="character-name-en"
                label={t('character.fields.name_en')}
                value={values.name_en}
                disabled={isSaving}
                onChange={(value) => updateValue('name_en', value)}
              />
              <TagInput
                id="character-aliases"
                label={t('character.fields.aliases')}
                values={values.aliases}
                disabled={isSaving}
                onChange={(items) => updateValue('aliases', items)}
              />
              <Field
                id="character-role"
                label={t('character.fields.role')}
                value={values.role}
                disabled={isSaving}
                onChange={(value) => updateValue('role', value)}
              />
              <Field
                id="character-age"
                label={t('character.fields.age')}
                value={values.age}
                disabled={isSaving}
                onChange={(value) => updateValue('age', value)}
              />
              <Field
                id="character-gender"
                label={t('character.fields.gender')}
                value={values.gender}
                disabled={isSaving}
                onChange={(value) => updateValue('gender', value)}
              />
              <label className="flex items-center gap-2 self-end rounded-md border border-input px-3 py-2 text-sm">
                <input
                  type="checkbox"
                  checked={values.is_organization}
                  disabled={isSaving}
                  onChange={(event) => updateValue('is_organization', event.target.checked)}
                />
                {t('character.fields.is_organization')}
              </label>
            </FormSection>
            <Separator />
            <FormSection title={t('character.groups.profile')}>
              <Field
                id="character-personality"
                label={t('character.fields.personality')}
                value={values.personality}
                disabled={isSaving}
                multiline
                onChange={(value) => updateValue('personality', value)}
              />
              <Field
                id="character-background"
                label={t('character.fields.background')}
                value={values.background}
                disabled={isSaving}
                multiline
                onChange={(value) => updateValue('background', value)}
                action={
                  <VoiceInputButton
                    value={values.background}
                    language={project.content_language}
                    disabled={isSaving}
                    onChange={(value) => updateValue('background', value)}
                  />
                }
              />
<IdentityAnchorFields value={values.visual_identity} disabled={isSaving} onChange={value=>updateValue('visual_identity',value)} />
              <Field
                id="character-appearance"
                label={t('character.fields.appearance')}
                value={values.appearance}
                disabled={isSaving}
                multiline
                onChange={(value) => updateValue('appearance', value)}
              />
              <Field
                id="character-relationships"
                label={t('character.fields.relationships')}
                value={values.relationships}
                disabled={isSaving}
                multiline
                onChange={(value) => updateValue('relationships', value)}
              />
              <Field
                id="character-motivation"
                label={t('character.fields.motivation')}
                value={values.motivation}
                disabled={isSaving}
                multiline
                onChange={(value) => updateValue('motivation', value)}
              />
              <Field
                id="character-inner-flaw"
                label={t('character.fields.inner_flaw')}
                value={values.inner_flaw}
                disabled={isSaving}
                multiline
                onChange={(value) => updateValue('inner_flaw', value)}
              />
              <Field
                id="character-identity"
                label={t('character.fields.identity_situation')}
                value={values.identity_situation}
                disabled={isSaving}
                onChange={(value) => updateValue('identity_situation', value)}
              />
              <Field
                id="character-realm"
                label={t('character.fields.realm')}
                value={values.realm}
                disabled={isSaving}
                onChange={(value) => updateValue('realm', value)}
              />
              <Field
                id="character-realm-level"
                label={t('character.fields.realm_level')}
                type="number"
                value={values.realm_level}
                disabled={isSaving}
                onChange={(value) => updateValue('realm_level', value)}
              />
              <Field
                id="character-voice"
                label={t('character.fields.voice_signature')}
                value={values.voice_signature}
                disabled={isSaving}
                onChange={(value) => updateValue('voice_signature', value)}
              />
              <TagInput
                id="character-traits"
                label={t('character.fields.traits')}
                values={values.traits}
                disabled={isSaving}
                onChange={(items) => updateValue('traits', items)}
              />
            </FormSection>
            {values.is_organization ? (
              <>
                <Separator />
                <FormSection title={t('character.groups.organization')}>
                  <Field
                    id="character-org-type"
                    label={t('character.fields.organization_type')}
                    value={values.organization_type}
                    disabled={isSaving}
                    onChange={(value) => updateValue('organization_type', value)}
                  />
                  <Field
                    id="character-org-purpose"
                    label={t('character.fields.organization_purpose')}
                    value={values.organization_purpose}
                    disabled={isSaving}
                    multiline
                    onChange={(value) => updateValue('organization_purpose', value)}
                  />
                  <Field
                    id="character-org-members"
                    label={t('character.fields.organization_members')}
                    value={values.organization_members}
                    disabled={isSaving}
                    multiline
                    onChange={(value) => updateValue('organization_members', value)}
                  />
                </FormSection>
              </>
            ) : null}
            <Separator />
            <FormSection title={t('character.groups.other')}>
              <Field
                id="character-notes"
                label={t('character.fields.notes')}
                value={values.notes}
                disabled={isSaving}
                multiline
                onChange={(value) => updateValue('notes', value)}
              />
              <Field
                id="character-avatar"
                label={t('character.fields.avatar_url')}
                value={values.avatar_url}
                disabled={isSaving}
                onChange={(value) => updateValue('avatar_url', value)}
              />
              <Field
                id="character-status"
                label={t('character.fields.status')}
                value={values.status}
                disabled={isSaving}
                onChange={(value) => updateValue('status', value)}
              />
              <Field
                id="character-role-type"
                label={t('character.fields.role_type')}
                value={values.role_type}
                disabled={isSaving}
                onChange={(value) => updateValue('role_type', value)}
              />
            </FormSection>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={isSaving}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={!values.name.trim() || isSaving}>
                {isSaving ? t('character.saving') : t('common.save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  )
}
